/* eslint-disable react/prop-types */
import React from 'react';
import { Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { ORGANIZATIONS } from '../../../../helpers/constants';

const useStyles = makeStyles({
  formControl: {
    color: '#ffffff',
  },
  select: {
    color: '#ffffff', // Custom color for selected value
  },
  dropdown: {
    maxHeight: 300,
  },
  // Custom arrow color
  icon: {
    color: '#ffffff', // Change the color of the arrow
  },
  disabledItem: {
    backgroundColor: '#2A2E39',
    color: '#FFFFFF',
    opacity: '1.0 !important',
  },
  enabledItem: {
    backgroundColor: '#3E414B !important',
    paddingLeft: '10px !important',
    color: '#FFFFFF',
    opacity: '1.0 !important',
  },
  disabledCheckbox: {
    backgroundColor: '#3E414B !important',
    paddingLeft: '10px !important',
    color: '#FFFFFF',
    opacity: '1.0 !important',
    '& .MuiSvgIcon-root': {
      backgroundColor: '#DAE1F947',
      color: 'transparent !important',
      borderRadius: 2,
    },
  },
  '@global': {
    '.MuiMenu-paper': {
      '&::-webkit-scrollbar': {
        width: '0.9em',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#595B64',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#2A2E39',
        borderRadius: '33px',
      },
    },
    '.MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#FFFFFF',
      },
      '&:hover fieldset': {
        borderColor: '#FFFFFF !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FFFFFF !important',
      },
    },
  },
  menuPaper: {
    maxHeight: '70%',
    overflowY: 'auto',
    backgroundColor: 'transparent',
    top: '80px !important',
    left: '10px !important',
    width: '195px !important',
    marginTop: '77px !important',
  },
  labelRoot: {
    color: '#FFFFFF !important',
  },
  selectedItem: {
    backgroundColor: '#656A7A !important',
  },
  singleItem: {
    paddingLeft: '25px !important',
  },
  hoverItem: {
    '&:hover': {
      backgroundColor: '#656A7A !important',
    },
  },
  checkboxRoot: {
    padding: '5px',
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
    '&.Mui-checked .MuiSvgIcon-root': {
      color: '#FF2C6CDE !important',
      border: 'transparent',
      borderRadius: 2,
    },
  },
});

const SalesStreamDropdown = props => {
  const {
    label,
    onChange,
    selectedValue,
    defaultValue,
    pricingAnalystItems,
    marketAnalystItems,
    teamsItems,
    disabled,
    organizations,
  } = props;
  const classes = useStyles();

  // Check if any user item is selected
  const isItemsDisabled = type => {
    if (selectedValue) {
      if (type === 'default' || type === 'pricing' || type === 'market') {
        return selectedValue.some(value => value.startsWith('team|') || value.startsWith('organization|'));
      }
      if (type === 'team') {
        return !selectedValue.some(value => value.startsWith('team|')) && selectedValue.length > 0;
      }
      if (type === 'organization') {
        return !selectedValue.some(value => value.startsWith('organization|')) && selectedValue.length > 0;
      }
      return false;
    }
    return false;
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" className={classes.formControl}>
      <InputLabel id="ss-filters-label" className={classes.labelRoot}>
        {label}
      </InputLabel>
      <Select
        labelId="ss-filters-label" // Link Select with InputLabel
        value={selectedValue}
        defaultValue={defaultValue}
        onChange={onChange}
        disabled={disabled}
        multiple
        data-testid="ss-select-list"
        renderValue={
          selected =>
            selected
              .map(value => {
                // Parse and display the name
                if (value.startsWith('pricing|')) {
                  return value.replace('pricing|', '');
                }
                if (value.startsWith('market|')) {
                  return value.replace('market|', '');
                }
                if (value.startsWith('team|')) {
                  return value.replace('team|', '');
                }
                if (value.startsWith('organization|')) {
                  const org = value.replace('organization|', '');
                  return ORGANIZATIONS[org];
                }
                return value;
              })
              .join(', ') // Join selected values with commas
        }
        label={label}
        MenuProps={{
          classes: { paper: classes.menuPaper },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        classes={{
          root: classes.dropdown,
          select: classes.select,
          icon: classes.icon, // Custom arrow color
        }}
      >
        <MenuItem disabled value="" className={classes.disabledItem}>
          Default
        </MenuItem>
        <MenuItem
          value={defaultValue}
          className={`
            ${isItemsDisabled('default') ? classes.disabledCheckbox : classes.enabledItem}
            ${classes.hoverItem}
          `}
          disabled={isItemsDisabled('default')}
        >
          <Checkbox
            checked={selectedValue.includes(defaultValue) || !selectedValue}
            classes={{
              root: classes.checkboxRoot,
            }}
          />
          <ListItemText primary="My Events" />
        </MenuItem>
        <MenuItem disabled value="" className={classes.disabledItem}>
          Pricing Analyst
        </MenuItem>
        {pricingAnalystItems.map(i => (
          <MenuItem
            value={`pricing|${i}`}
            key={i}
            className={`
            ${isItemsDisabled('pricing') ? classes.disabledCheckbox : classes.enabledItem}
            ${classes.hoverItem}
          `}
            disabled={isItemsDisabled('pricing')}
          >
            <Checkbox
              checked={selectedValue.includes(`pricing|${i}`)}
              classes={{
                root: classes.checkboxRoot,
                checked: classes.checkboxChecked,
              }}
            />
            <ListItemText primary={i} />
          </MenuItem>
        ))}
        <MenuItem disabled value="" className={classes.disabledItem}>
          Market Analyst
        </MenuItem>
        {marketAnalystItems.map(i => (
          <MenuItem
            value={`market|${i}`}
            key={i}
            className={`
              ${isItemsDisabled('market') ? classes.disabledCheckbox : classes.enabledItem}
            ${classes.hoverItem}
          `}
            disabled={isItemsDisabled('market')}
          >
            <Checkbox
              checked={selectedValue.includes(`market|${i}`)}
              classes={{
                root: classes.checkboxRoot,
              }}
            />
            <ListItemText primary={i} />
          </MenuItem>
        ))}
        <MenuItem disabled value="" className={classes.disabledItem}>
          RM Teams
        </MenuItem>
        {teamsItems.map(i => (
          <MenuItem
            value={`team|${i}`}
            key={i}
            className={`
            ${isItemsDisabled('team') ? classes.disabledCheckbox : classes.enabledItem}
            ${classes.hoverItem}
          `}
            disabled={isItemsDisabled('team')}
          >
            <Checkbox
              checked={selectedValue.includes(`team|${i}`)}
              classes={{
                root: classes.checkboxRoot,
              }}
            />
            <ListItemText primary={i} />
          </MenuItem>
        ))}
        <MenuItem disabled value="" className={classes.disabledItem}>
          Organizations
        </MenuItem>
        {organizations.map(i => (
          <MenuItem
            value={`organization|${i.id}`}
            key={i.id}
            className={`
            ${
              isItemsDisabled('organization') ||
              (selectedValue.length === 1 && !selectedValue.includes(`organization|${i.id}`))
                ? classes.disabledCheckbox
                : classes.enabledItem
            }
            ${classes.hoverItem}
          `}
            disabled={
              isItemsDisabled('organization') ||
              (selectedValue.length === 1 && !selectedValue.includes(`organization|${i.id}`))
            }
          >
            <Checkbox
              checked={selectedValue.includes(`organization|${i.id}`)}
              classes={{
                root: classes.checkboxRoot,
              }}
            />
            <ListItemText primary={i.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

SalesStreamDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  pricingAnalystItems: PropTypes.array.isRequired,
  marketAnalystItems: PropTypes.array.isRequired,
  teamsItems: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.any,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  organizations: PropTypes.array.isRequired,
};

SalesStreamDropdown.defaultProps = {
  disabled: false,
  selectedValue: null,
  defaultValue: '',
};

export default SalesStreamDropdown;
